import { memo, useEffect, useRef } from 'react';
import { mergeObject, replaceKey } from '../../utils';
import { formatDate, getDate } from '@euroland/libs';
import {
  HighChartWrapper,
  defaultOptions,
  tooltipOptions,
  plotOptions,
  xAxisLabelFormatCreator,
  normalizeData
} from '@euroland/react-high-chart';
import { useTemplateSetting } from '../../hooks/useCompanySettings';
import { DATE_SCREEN_READER, TOOL_TEMPLATE } from '../../constant/common';
import {i18n} from "@euroland/libs";
import { getCurrencyLabel } from '../../store/utils';
import { useSelector } from 'react-redux';
import { colorBlindModeSelector } from '../../store/slice/settings/selectors';
import useCurrencyName from '../../hooks/useCurrencyName';
import Highcharts from './setup';
import { formatNumberByInstrument } from '../../utils/format-number';
import useInstructionHighChart from '../../hooks/useInstructionHighChart';
/**
 * @typedef { import('../../..').IHistoricalPrices } IHistoricalPrices
 * @typedef { import('../../..').IInstrumentSetting } IInstrumentSetting
 * @typedef { import('../../..').IPeerSetting } IPeerSetting
 * @typedef {{series: IHistoricalPrices[]}} HighChartMultipleLineProps
 * @typedef { import('../../store/slice/trade').IInstrumentDataAndSettingSelectorReturn } IInstrumentDataAndSettingSelectorReturn
 * @typedef { import('../../..').CompanySetting } CompanySetting
 */

/**
 * @param {{
* series: IHistoricalPrices,
* instrumentDataAndSetting: IInstrumentDataAndSettingSelectorReturn
* }} param0
*/
const createOptions = ({series, instrumentDataAndSetting, closeData, volumeData, colorBlindMode, getCurrencyName, isQuadrata}) => (chartUtils) => {
  const { loadStyle, loadCommonStyle } = chartUtils
  const options = defaultOptions(chartUtils)

  const volumeUpColor = loadStyle('--chart-volume-up-color', `green`, 'hex color').get()
  const volumeDownColor = loadStyle('--chart-volume-down-color', `red`, 'hex color').get()

  const instrumentId = series.instrumentId;
  const instrument = instrumentDataAndSetting[instrumentId]
  const currency = getCurrencyLabel(series.currency)
  const tooltip = mergeObject(tooltipOptions(chartUtils), {
    formatter: function () {
      return `
        <div class="custom-tooltip">
          <div class="custom-tooltip__time" aria-label="${formatDate(this.x, '', DATE_SCREEN_READER)}">${formatDate(this.x, 'shortDate')}</div>
          <div class="custom-tooltip__item">
              <span class="custom-tooltip__label" aria-label="${this.series.options.ariaName}">${this.series.name}</span>
              <span class="custom-tooltip__value">${formatNumberByInstrument(this.y, instrumentId)}</span>
              <span class="custom-tooltip__currency" aria-label="${getCurrencyName(series.currency)}">(${currency})</span>
          </div>
          <div class="custom-tooltip__item">
              <span class="custom-tooltip__label">${i18n.translate('volume')}</span>
              <span class="custom-tooltip__value">${formatNumberByInstrument(volumeData.dataIndex[this.x], instrumentId, { decimalDigits: 0 })}</span>
          </div>
        </div>
      `
    },
    shared: false,
  })

  const xAxis = {
    type: 'datetime',
    labels: {
      formatter: xAxisLabelFormatCreator({formatDate, getDate})
    }
  }

  const ariaName = isQuadrata ? instrument.data.shareName : `${instrument.data.ticker} ${instrument.data.marketName}`;
  const currencyName = getCurrencyName(series.currency);

  const yAxis = [
    mergeObject(options.yAxis, {
    
      title: {
        text: currency,
        style: {
          'padding-left': '0.5rem'
        }
      },
      plotLines: [
        {
          value: closeData.max,
          width: loadStyle('--chart-y-axis-plot-lines-max-width', 1, 'hex color').toNumber(),
          color: loadStyle('--chart-y-axis-plot-lines-max-color', 'green', 'hex color').get(),
          dashStyle: loadStyle('--chart-y-axis-plot-lines-max-dash-style', 'Dash', 'dashStyle').get(),
          zIndex: 2
        },
        {
          value: closeData.min,
          width: loadStyle('--chart-y-axis-plot-lines-min-width', 1, 'hex color').toNumber(),
          color: loadStyle('--chart-y-axis-plot-lines-min-color', 'red', 'hex color').get(),
          dashStyle: loadStyle('--chart-y-axis-plot-lines-min-dash-style', 'Dash', 'dashStyle').get(),
          zIndex: 2
        },
      ],
      labels: {
        formatter: function () {
          return  formatNumberByInstrument(this.value, instrumentId)
        }
      }
    }),
    {
      gridLineWidth: 0, // remove grid line in volumn series
      title: {
        enabled: false,
      },
      height: loadStyle('--chart-y-axis-volume-height', '20%', 'percent]').get(),
      top: loadStyle('--chart-y-axis-volume-top', '80%', 'percent').get(),
      labels: {
        enabled: false,
      },
    },
  ];
  const result = mergeObject(options, {
    accessibility: {
      enabled: true,
      series: {
        pointDescriptionEnabledThreshold: false,
      },
      point: {
        descriptionFormatter: (point) => {
          const volume = volumeData.dataIndex[point.x];
          const date = formatDate(point.x, DATE_SCREEN_READER);
          return i18n.translate('chartLineVolumePointLabelAccessibility', date, ariaName ,point.y, currencyName, volume);
        },
      }
    },
    tooltip,
    xAxis,
    yAxis,
    plotOptions: {
      series: {
        connectNulls: true
      },
      column: { tooltip: { enabled: false }, enableMouseTracking: false },
      spline: plotOptions.spline(chartUtils)
    },
    series: [{
      type: 'column',
      name: 'Volume',
      pointWidth: loadStyle('--chart-volume-column-width', undefined, 'px').toNumber(),
      borderRadius: loadStyle('--chart-volume-column-border-radius', 0, 'px').toNumber(),
      borderColor: loadStyle('--chart-volume-column-border-color', '#ffffff', 'hex').get(),
      borderWidth: loadStyle('--chart-volume-column-border-width', 1, 'px').toNumber(),
      groupPadding: loadStyle('--chart-volume-column-group-padding', 0.2, 'px').toNumber(),
      pointPadding: loadStyle('--chart-volume-column-point-padding', 0.1, 'px').toNumber(),
      maxPointWidth: loadStyle('--chart-volume-column-max-width', undefined, 'px').toNumber(),
      data: volumeData.data.map((item, index) => {
        const prevIndex = volumeData.data[index === 0 ? 0 : (index - 1)][0]
        const currentIndex = item[0]
        const isUp = closeData.dataIndex[currentIndex] >= closeData.dataIndex[prevIndex];
        let color = isUp ? volumeUpColor : volumeDownColor
        return {
          color: color,
          x: item[0],
          y: item[1],
        }
      }),
      yAxis: 1,
    }, {
      // color: loadStyle('--chart-line-volume-line-color', '#2F80ED', 'set color for line').get(),
      color: instrument.setting.color,
      type: 'spline',
      name: isQuadrata ? instrument.data.shareName : replaceKey(i18n.translate('shareName'), [instrument.data.ticker, instrument.data.marketAbbreviation]),
      ariaName,
      data: closeData.data.map((item, index) => {
        const result = {
          x: item[0],
          y: item[1]
        }


        if (closeData.maxIndex === item[0] || closeData.minIndex === item[0]) {
          result.marker = { enabled: true }
        }

        return result;
      }),
      dataLabels: {
        enabled: true,
        useHTML: true,
        formatter: function () {
          const value = this.point.y;

          if (this.point.x === closeData.maxIndex) return `
            <div class="line-point high">
                <span class="line-point__label">${i18n.translate('high')}</span>
                <span class="line-point__value">${formatNumberByInstrument(value, instrumentId)}</span>
                <span class="line-point__currency">${replaceKey(i18n.translate('currencyFormat'), [currency])}</span>
            </div>`;

          if (this.point.x === closeData.minIndex) return `
            <div class="line-point low">
                <span class="line-point__label">${i18n.translate('low')}</span>
                <span class="line-point__value">${formatNumberByInstrument(value, instrumentId)}</span>
                <span class="line-point__currency">${replaceKey(i18n.translate('currencyFormat'), [currency])}</span>
            </div>`;
        }
      },
      yAxis: 0,
    }]
  })
  return result
}

/**
 * @typedef { import('../../..').IHistoricalPrices} HighChartsLineAndVolumeSeries
 */
export const HighChartsLineAndVolumeVictor = memo(( /** @type {{series: HighChartsLineAndVolumeSeries }} */ {series, instrumentDataAndSetting}) => {
  const closeData = normalizeData(series.datas.map(item => ([new Date(item.date).getTime(), item.close])))
  const volumeData = normalizeData(series.datas.map(item => ([new Date(item.date).getTime(), item.volume])))
  const colorBlindMode = useSelector(colorBlindModeSelector)
  const getCurrencyName = useCurrencyName();
  const chatContainerRef = useRef()
  useInstructionHighChart(chatContainerRef)
  return (
    <HighChartWrapper
      className='high-charts-line-and-volume'
      createOptions={createOptions({series, instrumentDataAndSetting, closeData, volumeData, colorBlindMode, getCurrencyName})}
      highCharts={Highcharts}
      allowChartUpdate
      immutable={true}
      isRtl={window.appSettings.isRtl}
      ref={chatContainerRef}
    />
  )
}, (prev, next) => {
  return prev.series === next.series
})

/**
 * @param {{
* series: IHistoricalPrices,
* instrumentDataAndSetting: IInstrumentDataAndSettingSelectorReturn
* }} param0
*/
const createOptionsQuadrata = (options) => (chartUtils) => {
  const result = createOptions(options)(chartUtils)
  const { series, volumeData, getCurrencyName } = options
  const currency = getCurrencyLabel(series.currency)
  const instrumentId = series.instrumentId;

  result.tooltip.formatter = function () {
    return `
      <div class="custom-tooltip">
        <div class="custom-tooltip__time" aria-label="${formatDate(this.x, '', DATE_SCREEN_READER)}">${formatDate(this.x, 'shortDate')}</div>
        <div class="custom-tooltip__item">
          <span class="custom-tooltip__content">
            <span class="custom-tooltip__label" aria-label="${this.series.options.ariaName}">${this.series.name}</span>
            <span class="custom-tooltip__wrap">
              <span class="custom-tooltip__currency" aria-label="${getCurrencyName(series.currency)}">${currency}</span>
              <span class="custom-tooltip__value">${formatNumberByInstrument(this.y, instrumentId)}</span>
            </span>
            <span class="custom-tooltip__wrap">
              <span class="custom-tooltip__currency">${i18n.translate('volume')}</span>
              <span class="custom-tooltip__value">${formatNumberByInstrument(volumeData.dataIndex[this.x], instrumentId, { decimalDigits: 0 })}</span>
            </span>
          </span>
        </div>
      </div>
    `
  }

  // we delete all options plotLines dataLabels item.marker in template quadrata
  // delete result.yAxis[0].plotLines
  // delete result.series[1].dataLabels
  // result.series[1].data.map(item => {
  //   if('marker' in item) {
  //     delete item.marker
  //   }
  //   return item;
  // })
  return result;

}
/**
 * @typedef { import('../../..').IHistoricalPrices} HighChartsLineAndVolumeSeries
 */
export const HighChartsLineAndVolumeQuadrata = memo(( /** @type {{series: HighChartsLineAndVolumeSeries }} */ {series, instrumentDataAndSetting}) => {

  const closeData = normalizeData(series.datas.map(item => ([new Date(item.date).getTime(), item.close])))
  const volumeData = normalizeData(series.datas.map(item => ([new Date(item.date).getTime(), item.volume])))
  const colorBlindMode = useSelector(colorBlindModeSelector)
  const getCurrencyName = useCurrencyName()
  const chatContainerRef = useRef();

  useInstructionHighChart(chatContainerRef)
  return (
    <HighChartWrapper
      className='high-charts-line-and-volume high-charts-line-and-volume__quadrata'
      createOptions={createOptionsQuadrata({series, instrumentDataAndSetting, closeData, volumeData, colorBlindMode, getCurrencyName, isQuadrata: true})}
      highCharts={Highcharts}
      isRtl={window.appSettings.isRtl}
      allowChartUpdate
      immutable={true}
      ref={chatContainerRef}
    />
  )
}, (prev, next) => {
  return prev.series === next.series
})

export const HighChartsLineAndVolume = (props) => {
  const template = useTemplateSetting();

  switch(template) {
    case TOOL_TEMPLATE.victor:
      return <HighChartsLineAndVolumeVictor {...props} />;
    case TOOL_TEMPLATE.quadrata:
      return <HighChartsLineAndVolumeQuadrata {...props} />
    default:
      throw new Error(`Template: ${template} do not exist.`)
  }
}

// export default HighChartsLineAndVolume
